/* eslint no-param-reassign: 0 */

import _ from 'underscore';
import Promise from 'bluebird';
import { getODataErrors } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import DataStore from '@bingads-webui-component-legacy/js-data-campaign/index';
import edmCampaign from '@bingads-webui-component-legacy/edm-campaign/index';
import BaseRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/base';
import sharedDataModelUtil from '@bingads-webui-campaign-legacy/adextension-shared/util/sharedDataModelConvert';
import dataModelUtil from '@bingads-webui-campaign-legacy/adextension-image/util/dataModelConvert';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/feature/adextension/shared');
const s$i18n = window.globalLegacyI18n.get('$/schema-i18n!campaign');

class BadResponseError {
  constructor(schema) {
    this.type = schema.name;
  }

  toString() {
    return i18n.get('AdExtensionEditor_FailedToSave', {
      type: s$i18n.type(this.type),
    });
  }
}

const isCacheValid = {};

export default class ImageAdExtensionsRepository extends BaseRepository {
  constructor(options) {
    super(options);
    this._schema = options.schema;
    this._store = DataStore[this._schema.name];
    this.dataFormatter = options.extensionDataFormatter || _.identity;
    this.afterChangeComitted = options.afterChangeComitted;

    if (!_.has(isCacheValid, this._schema.name)) {
      isCacheValid[this._schema.name] = false;
    }
  }

  storeOptions() {
    return {
      entitySet: edmCampaign.currentAccount.AdExtensions,
      cacheResponse: false,
    };
  }

  findStoreOptions() {
    return {
      entitySet: edmCampaign.currentAccount.AdExtensions,
      cacheResponse: false,
      queryParameters: {
        query: {
          $expand: 'Model.ImageAdExtension/Images',
        },
      },
    };
  }

  updateStoreOptions() {
    return {
      entitySet: edmCampaign.currentAccount.AdExtensions,
      cacheResponse: false,
    };
  }

  isNew(entity) {
    return _.isEmpty(entity) || _.isUndefined(entity[this._schema.key]);
  }

  create(params) {
    let schema = this._schema;
    let key = this._schema.key;

    return this._store.create(params, this.storeOptions())
      .catch((response) => {
        if (response && !_.isEmpty(response.errors)) {
          throw getODataErrors(response.errors);
        }

        throw new BadResponseError(schema);
      })
      .then((data) => {
        isCacheValid[this._schema.name] = false;

        if (_.isNumber(data.value) && data.value > 0) {
          let ret = _.clone(params);

          ret[key] = data.value;

          return ret;
        }

        throw new BadResponseError(schema);
      })
      .tap(this.afterChangeComitted);
  }

  update(params) {
    let schema = this._schema;

    return this._store.update(params.Id, params, this.updateStoreOptions())
      .catch((response) => {
        if (response && !_.isEmpty(response.errors)) {
          throw getODataErrors(response.errors);
        }

        throw new BadResponseError(schema);
      })
      .then((data) => {
        isCacheValid[this._schema.name] = false;

        if (data.value === 0) {
          return params;
        }

        throw new BadResponseError(schema);
      })
      .tap(this.afterChangeComitted);
  }

  find(id) {
    return this._store.find(Number(id), this.findStoreOptions()).then((item) => {
      if (item.Images.length > 0) {
        return Promise.resolve(item);
      }

      return dataModelUtil.getImagesFromIdsAsync(item.ImageIdsStr).then((images) => {
        item.Images = images;

        return item;
      });
    });
  }

  findAll(params) {
    let formatter = this.dataFormatter;
    let options = this.storeOptions();

    _.extend(options, { bypassCache: true });

    params = this.adaptParams(params);
    params.expand = ['Model.ImageAdExtension/Images'];

    if (!isCacheValid[this._schema.name]) {
      options.headers = _.extend(options.headers || {},
        {'x-ms-refreshcampaigncache': 'true'});
    }

    return this._store
      .findAll(params, options)
      .then((data) => {
        isCacheValid[this._schema.name] = true;
        // This works for the legacy data with image that we do not support (1.20: 1)
        return sharedDataModelUtil.migrateLegacyDataAsync(data);
      }).then((data) => {
        return Promise.props({
          items: formatter(data),
          total: data.totalCount,
        });
      });
  }

  destroy(id) {
    return this._store
      .destroy(Number(id), this.storeOptions())
      .then((data) => {
        isCacheValid[this._schema.name] = false;
        return data;
      })
      .tap(this.afterChangeComitted);
  }
}

ImageAdExtensionsRepository.prototype.BadResponseError = BadResponseError;
